import { useEffect, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { topicImportFromCsvSchema } from "../schema/topicImportFromCsvSchema";
import { ApiErrorResponse } from "../../../../types/ApiErrorResponse";
import AlertMessage from "../../../../components/AlertMessage";
import { TopicImportCsvDto } from "../types";
import { importTopicsFromCsv } from "../api/topicApi";
import { analyzeCsv } from "../../../../utils/analysisCSV";
import { GenericSaveButton } from "../../../../components/common/GenericSaveButton";
import { useQueryClient } from "@tanstack/react-query";
import { CsvAnalysis } from "../../../../types/CsvAnalysis";

interface TopicImportFromCSVProp {
    setShowImportFromCsvPage: (showPage: boolean) => void;
}

export default function TopicImportFromCSV ({
    setShowImportFromCsvPage
}: TopicImportFromCSVProp) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [formData, setFormData] = useState<TopicImportCsvDto>({
        description: "",
        file: null
    });
    const [validationErrors, setValidationErrors] = useState<{ [key: string]: string | undefined }>({});
    const [errorMessage, setErrorMessage] = useState<string | null>();
    const [totalRows, setTotalRows] = useState<number | null>(null);

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
            });
    }, [])

    const uploadFile = async (file: File) => {
        setTotalRows(null);
        setErrorMessage(null);
        setValidationErrors({});

        try {
            const analysis: CsvAnalysis = await analyzeCsv(file);
            
            if (analysis.totalColumns != 5) {
                setValidationErrors({
                    file: t("common:form.invalidFileStructure"),
                })

                return;
            }

            setFormData((prev) => ({
                ...prev,
                file
            }));
            setTotalRows(analysis.totalRows);
        } catch (error: any) {
            setErrorMessage(error.message ?? error[0].message);
        }
    }

    const handleSubmit = async () => {
        const schema = topicImportFromCsvSchema(t);
        setValidationErrors({});
        try {
            await schema.validate(formData, { abortEarly: false });
        } catch (err: any) {
            const errors: { [key: string]: string | undefined } = {};
            if (Array.isArray(err?.inner) && err.inner.length > 0) {
                err.inner.forEach((error: any) => {
                    if (error.path) {
                        errors[error.path] = error.message;
                    }
                });
            } else if (err?.path) {
                errors[err.path] = err.message;
            }
            setValidationErrors(errors);
            return;
        }

        setErrorMessage(null);
        setIsLoading(true);
        
        try {
            await importTopicsFromCsv(formData);
            setShowImportFromCsvPage(false);
            await queryClient.invalidateQueries({ queryKey: ["adminTopicsTree"] });
        } catch (error) {
            const apiError = error as ApiErrorResponse;
            setErrorMessage(apiError?.message ?? t("common:requestError.errorBadRequest"));
        } finally {
            setIsLoading(false);
        }
    }

    return(<>
        <Row className="mb-2">
            <Col xs={12} md={8}>
                <h2>{t("survey:load.buttonNewLoad")}</h2>
            </Col>
            <Col xs={4} md={4} className="text-end">
                <GenericSaveButton
                    onClick={handleSubmit}
                    onCancelClick={() => setShowImportFromCsvPage(false)}
                    saveLabel={isLoading ? t("common:buttons.buttonImportingProgress") : t("common:buttons.buttonImport")}
                    cancelLabel={t("common:buttons.buttonCancel")}
                    disabled={isLoading}
                />
            </Col>
        </Row>
        <Form>
            <Row>
                {errorMessage && (
                    <Col xs={12}>
                        <AlertMessage
                            message={errorMessage || t("common:requestError.errorBadRequest")}
                            type="danger"
                        />
                    </Col>
                )}
            </Row>
            <Row>
                <Col xs={12} md={12}>
                    <Form.Group>
                        <Form.Label>{t("common:form.fields.file")}</Form.Label>
                        <Form.Control
                            isInvalid={!!validationErrors.file}
                            type="file"
                            accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                            onChange={async (e) => {
                                const target = e.target as HTMLInputElement;
                                const selectedFile = target.files?.[0];
                                if (selectedFile) {
                                    await uploadFile(selectedFile);
                                }
                            }}
                        />
                        <Form.Control.Feedback type="invalid">
                            {validationErrors.file}
                        </Form.Control.Feedback>
                        {(!validationErrors.file && totalRows != null) &&
                            <span>{t("topic:csvImportTotalRows", {totalRows: totalRows})}</span>
                        }
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col xs={12} md={12}>
                    <Form.Group>
                        <Form.Label>{t("topic:uploadDescription")}</Form.Label>
                        <Form.Control
                            type="text"
                            value={formData.description || ""}
                            isInvalid={!!validationErrors.description}
                            onChange={(e) => setFormData((prev) => ({
                                ...prev,
                                description: e.target.value
                            }))}
                        />
                        <Form.Control.Feedback type="invalid">
                            {validationErrors.description}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Col>
            </Row>
        </Form>
    </>);
}